import React, {
  useMemo,
} from 'react';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import {
  AddendumDTO,
  FileResultDTO,
  GetApiVisitNoteGetUpcomingAppointmentsByPatientIdPatientIdParams,
  None,
  PatientDTO,
  PatientMultimediaBulkUploadRequestDTO,
  PatientMultimediaBulkUploadResponseDTO,
  PatientVisitDTO,
  SettingsDTO,
  UpcomingAppointmentsPdfRequestDTO,
} from 'dtos';
import {
  patchApiPatientUpdatePatient,
  getApiVisitNoteGetHistoricalVisitsByPatientIdPatientId,
  getApiVisitNoteGetAddendumFinalizedVisitNoteId,
  getApiVisitNoteGetUpcomingAppointmentsByPatientIdPatientId,
  postApiVisitNotePrintUpcomingAppointmentsPdf,
  putApiVisitNoteAddAddendum,
  getApiSettingsGetPatientMultimediaFilterSettings,
  postApiMultimediaBulkUploadPatientMultimedia,
  getApiCommunicationGetPhoneNumberTag,
  getApiPatientGetPatientByIdId,
} from 'endpoints';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import {
  serializePatient,
  transformPatient,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  isEmptyString,
  isNilOrZero,
} from '@populateui/utils';
import {
  useNotify,
} from 'core/hooks';
import {
  normalizePhoneNumber,
} from 'pages/Dashboard/utils/helper';
import {
  isNoAccessError,
  NoAccessOptions,
} from 'utils/errors';

// patient charts
export function useUpdatePatientChart():
  UseMutationResult<PatientDTO, Error, Partial<PatientForm>> {
  return useMutation<PatientDTO, Error, Partial<PatientForm>>({
    mutationFn: async (data: Partial<PatientForm>) => {
      const serialized = serializePatient(data);
      return patchApiPatientUpdatePatient(serialized);
    },
  });
}

export function useGetPatientInfo(
  id: number,
  { throwOnNoAccess = false }: NoAccessOptions = {},
): UseQueryResult<PatientForm, Error> {
  return useQuery<PatientForm, Error>({
    queryKey: ['patient', id],
    queryFn: async () => {
      const response = await getApiPatientGetPatientByIdId(id);
      return transformPatient(response);
    },
    enabled: !isNilOrZero(id),
    throwOnError: throwOnNoAccess && isNoAccessError,
  });
}

export function useGetPhoneNumberTag(
  phoneNumber: string | null | undefined,
) {
  const normalizedPhone = useMemo(
    () =>
      normalizePhoneNumber(phoneNumber ?? ''),
    [phoneNumber],
  );

  const { data: tagResponse, isLoading } = useQuery({
    queryKey: ['phone-number-tag', normalizedPhone],
    queryFn: () => getApiCommunicationGetPhoneNumberTag({ phoneNumber: normalizedPhone ?? '' }),
    enabled: !isEmptyString(normalizedPhone ?? ''),
    gcTime: 600000, // 10 minutes
  });

  const phoneTag = useMemo(
    () => tagResponse?.tag,
    [tagResponse],
  );

  return {
    phoneTag: isLoading ? undefined : phoneTag,
    isLoading,
  };
}

// visits
export const useGetUpcomingAppointments = (
  id: number = 0,
  params?: GetApiVisitNoteGetUpcomingAppointmentsByPatientIdPatientIdParams,
): UseQueryResult<
  PatientVisitDTO[], Error> => (
  useQuery<PatientVisitDTO[], Error>({
    queryKey: ['upcomingVisits', id, params?.includeCancelledNoShow ?? false],
    queryFn: () => getApiVisitNoteGetUpcomingAppointmentsByPatientIdPatientId(id, params),
    enabled: id > 0,
  })
);

export const useGetHistoricalVisitNotesByPatientId = (id: number = 0): UseQueryResult<
  PatientVisitDTO[], Error> => (
  useQuery<PatientVisitDTO[], Error>({
    queryKey: ['pastVisits', id],
    queryFn: () => getApiVisitNoteGetHistoricalVisitsByPatientIdPatientId(id),
    enabled: id > 0,
  })
);

export function usePrintUpcomingAppointmentsPdf():
  UseMutationResult<FileResultDTO, Error, UpcomingAppointmentsPdfRequestDTO> {
  return useMutation<FileResultDTO, Error, UpcomingAppointmentsPdfRequestDTO>({
    mutationFn: (data) => postApiVisitNotePrintUpcomingAppointmentsPdf(data),
  });
}

// addendum
export function useGetAddendum(visitNoteId: number): UseQueryResult<AddendumDTO, Error> {
  return useQuery<AddendumDTO, Error>({
    queryKey: ['addendum', visitNoteId],
    queryFn: () => getApiVisitNoteGetAddendumFinalizedVisitNoteId(visitNoteId),
  });
}

export function useUpsertAddendum(): UseMutationResult<None, Error, AddendumDTO> {
  return useMutation<None, Error, AddendumDTO>({
    mutationFn: (data) => putApiVisitNoteAddAddendum(data),
  });
}

// patientMultimedia
export function useGetPatientMultimediaFilterMetaComponents() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['patient-multimedia-filter-settings'],
    fetchFn: getApiSettingsGetPatientMultimediaFilterSettings,
  });

  return normalizeFilterableSettings(data?.filterableSettings ?? []);
}

export function useUploadBulkMultimedia(): UseMutationResult<
  PatientMultimediaBulkUploadResponseDTO, Error, PatientMultimediaBulkUploadRequestDTO> {
  return useMutation<
    PatientMultimediaBulkUploadResponseDTO,
    Error,
    PatientMultimediaBulkUploadRequestDTO
  >({
    mutationFn: (data) => postApiMultimediaBulkUploadPatientMultimedia(data),
  });
}

export function useHandleMediaUploadResponse() {
  const notify = useNotify();

  const handleMediaUploadResponse = (
    response: PatientMultimediaBulkUploadResponseDTO,
    successMessage: string = 'Media uploaded successfully',
    errorMessage: string = 'Media upload failed',
  ) => {
    const uploadedIds = response?.uploadedMediaIds ?? [];
    const errors = response?.errors ?? [];

    if (errors.length > 0) {
      notify({
        timeout: 100000,
        variant: 'error',
        message: (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{errorMessage}</p>
            <ul>
              {errors.map((error) => (
                <li key={error.fileName}>
                  <span>{error.error}</span>
                </li>
              ))}
            </ul>
          </div>
        ),
      });
    }

    if (uploadedIds.length > 0) {
      notify({
        variant: 'success',
        message: successMessage,
      });
    }

    return uploadedIds?.length > 0;
  };

  return { handleMediaUploadResponse };
}
